import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { MITPService } from "../../../services/mitp.service";
import { ProtocolNote, ProtocolNoteRequest, ProtocolNotesPage } from "./entities/protocol-note";
import { EProtocolNoteStatus, ProtocolNoteStatusFilter } from "./enums/eprotocol-note-status";

export interface MyProtocolNotesQuery {
	status?: ProtocolNoteStatusFilter;
	protocol?: number;
	maxPerPage?: number;
	currentPage?: number;
}

@Injectable({
	providedIn: "root",
})
export class ProtocolNoteService {
	private httpClient = inject(HttpClient);
	private mitp = inject(MITPService);

	/**
	 * Les notes sont rattachées à une version publiée, pas au protocole. Les deux
	 * routes ci-dessous laissent le serveur résoudre la dernière version lui-même :
	 * le visualiseur affiche toujours la version courante et n'a aucun moyen
	 * fiable de connaître son identifiant après une navigation `mitp://`.
	 */
	getProtocolNotes(protocolId: number, status?: ProtocolNoteStatusFilter): Observable<ProtocolNote[]> {
		return this.httpClient.get<ProtocolNote[]>(this.mitp.Protocols() + `/${protocolId}/notes`, {
			params: this.statusParams(status),
		});
	}

	createProtocolNote(protocolId: number, payload: ProtocolNoteRequest): Observable<ProtocolNote> {
		return this.httpClient.post<ProtocolNote>(this.mitp.Protocols() + `/${protocolId}/notes`, payload);
	}

	/** Variantes explicitement versionnées, pour consulter une version qui n'est plus la courante. */
	getUpdateNotes(updateId: number, status?: ProtocolNoteStatusFilter): Observable<ProtocolNote[]> {
		return this.httpClient.get<ProtocolNote[]>(this.mitp.ProtocolUpdates() + `/${updateId}/notes`, {
			params: this.statusParams(status),
		});
	}

	createUpdateNote(updateId: number, payload: ProtocolNoteRequest): Observable<ProtocolNote> {
		return this.httpClient.post<ProtocolNote>(this.mitp.ProtocolUpdates() + `/${updateId}/notes`, payload);
	}

	/** Les notes de l'utilisateur, tous protocoles et toutes versions confondus. */
	getMyNotes(query: MyProtocolNotesQuery = {}): Observable<ProtocolNotesPage> {
		let params = this.statusParams(query.status);
		if (query.protocol != null) {
			params = params.set("protocol", query.protocol);
		}
		if (query.maxPerPage != null) {
			params = params.set("maxPerPage", query.maxPerPage);
		}
		if (query.currentPage != null) {
			params = params.set("currentPage", query.currentPage);
		}
		return this.httpClient.get<ProtocolNotesPage>(this.mitp.ProtocolNotes(), { params: params });
	}

	getNote(id: number): Observable<ProtocolNote> {
		return this.httpClient.get<ProtocolNote>(this.mitp.ProtocolNotes() + `/${id}`);
	}

	/** Remplacement complet : le corps doit toujours porter la note entière. */
	updateNote(id: number, payload: ProtocolNoteRequest): Observable<ProtocolNote> {
		return this.httpClient.put<ProtocolNote>(this.mitp.ProtocolNotes() + `/${id}`, payload);
	}

	deleteNote(id: number): Observable<HttpResponse<null>> {
		return this.httpClient.delete<null>(this.mitp.ProtocolNotes() + `/${id}`, {
			observe: "response",
		});
	}

	/** `shared` est envoyé explicitement : un corps vide bascule l'état côté serveur. */
	shareNote(id: number, shared: boolean): Observable<ProtocolNote> {
		return this.httpClient.post<ProtocolNote>(this.mitp.ProtocolNotes() + `/${id}/share`, { shared: shared });
	}

	archiveNote(id: number): Observable<ProtocolNote> {
		return this.httpClient.post<ProtocolNote>(this.mitp.ProtocolNotes() + `/${id}/archive`, null);
	}

	restoreNote(id: number): Observable<ProtocolNote> {
		return this.httpClient.post<ProtocolNote>(this.mitp.ProtocolNotes() + `/${id}/restore`, null);
	}

	/** L'API filtre sur les notes actives par défaut : on ne transmet que ce qui s'en écarte. */
	private statusParams(status?: ProtocolNoteStatusFilter): HttpParams {
		let params = new HttpParams();
		if (status != null && status !== EProtocolNoteStatus.ACTIVE) {
			params = params.set("status", status);
		}
		return params;
	}
}
